import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, Send, Printer } from 'lucide-react';

const ResultsTab = ({
  exams,
  classes,
  selectedExam,
  selectedClass,
  classResult,
  onSelectExam,
  onSelectClass,
  onViewResults,
  onPublishResult,
  onPrintReportCard
}) => {
  const prepareReportCardData = (r) => ({
    ...r, 
    student_name: r.name || r.student_name,
    roll_number: r.roll_number,
    class_name: classResult?.class_name || '',
    exam_name: classResult?.exam_name || selectedExam?.name,
    school_name: classResult?.school_name || '',
    school_address: classResult?.school_address || '',
    school_logo: classResult?.school_logo || '',
    percentage: r.percentage || 0,
    overall_grade: r.grade,
    status: r.result || r.status,
    total_obtained: r.total_obtained || 0,
    total_max_marks: r.total_max || 0,
    rank: r.rank
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">📊 Results & Report Cards</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>View Results</CardTitle>
          <CardDescription>Select exam and class to view results and print report cards</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Select Exam</Label>
              <Select value={selectedExam?.id || ''} onValueChange={onSelectExam}>
                <SelectTrigger><SelectValue placeholder="Select Exam" /></SelectTrigger>
                <SelectContent>
                  {exams.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Class</Label>
              <Select value={selectedClass} onValueChange={onSelectClass}>
                <SelectTrigger><SelectValue placeholder="Select Class" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button 
                onClick={onViewResults} 
                disabled={!selectedExam || !selectedClass}
                className="flex-1"
              >
                <Eye className="h-4 w-4 mr-2" /> View Results
              </Button>
              {selectedExam?.status !== 'result_published' && (
                <Button variant="secondary" onClick={() => onPublishResult(selectedExam?.id)} disabled={!selectedExam}>
                  <Send className="h-4 w-4 mr-2" /> Publish
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {classResult && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Class Results - {selectedExam?.name}</CardTitle>
              <Button variant="outline" onClick={() => (classResult.students || classResult.results)?.forEach(r => onPrintReportCard(prepareReportCardData(r)))}>
                <Printer className="h-4 w-4 mr-2" /> Print All Report Cards
              </Button>
            </div>
            {classResult.summary && (
              <div className="grid grid-cols-4 gap-4 mt-4 text-sm">
                <div className="p-3 bg-muted rounded">
                  <div className="font-semibold">Total Students</div>
                  <div className="text-2xl font-bold">{classResult.summary.total_students}</div>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <div className="font-semibold">Passed</div>
                  <div className="text-2xl font-bold text-green-600">{classResult.summary.passed}</div>
                </div>
                <div className="p-3 bg-red-50 rounded">
                  <div className="font-semibold">Failed</div>
                  <div className="text-2xl font-bold text-red-600">{classResult.summary.failed}</div>
                </div>
                <div className="p-3 bg-blue-50 rounded">
                  <div className="font-semibold">Class Average</div>
                  <div className="text-2xl font-bold text-blue-600">{classResult.summary.class_average?.toFixed(1)}%</div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left">Rank</th>
                  <th className="p-3 text-left">Roll No</th>
                  <th className="p-3 text-left">Student Name</th>
                  <th className="p-3 text-left">Total</th>
                  <th className="p-3 text-left">Percentage</th>
                  <th className="p-3 text-left">Grade</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(classResult.students || classResult.results)?.map((r, idx) => (
                  <tr key={r.student_id} className="border-t">
                    <td className="p-3 font-bold">#{r.rank || idx + 1}</td>
                    <td className="p-3">{r.roll_number}</td>
                    <td className="p-3 font-medium">{r.name || r.student_name}</td>
                    <td className="p-3">{r.total_obtained}/{r.total_max || r.total_max_marks}</td>
                    <td className="p-3">{r.percentage?.toFixed(1)}%</td>
                    <td className="p-3"><Badge>{r.grade || r.overall_grade}</Badge></td>
                    <td className="p-3">
                      <Badge variant={(r.result === 'PASS' || r.status === 'Pass') ? 'default' : 'destructive'}>
                        {r.result || r.status}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Button size="sm" variant="outline" onClick={() => onPrintReportCard(prepareReportCardData(r))}>
                        <Printer className="h-3 w-3 mr-1" /> Report Card
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ResultsTab;
